import { computed, readonly, ref, toValue } from "vue";
import store from "@/store/index.js";
import { isTenantLibrariesFlagEnabled, isTenantManager } from "./authorization";
import { useCurrentRoute } from "./useOptionsApiRouter";

// Don't recompute this every time in the composable.
const PROGRAM_MANAGER_VIEW_MORE_REG_EXP = /\/program-manager\/\w{3,25}(\/view-more)?$/g;

const hasMultipleEntities = computed(() => {
  const entities = store.getters.getEntityIds;
  return entities?.length > 1;
});

/**
 * A Vue composable that retrieves and returns the default current selected
 * tenant owner ID using store getters. The value is reactive and
 * read-only.
 *
 * This composable accesses the `getEntityIds` getter from the `store` to
 * retrieve an array of tenant owner IDs, and selects the first item
 * as the current selected tenant owner ID. The resultant value is
 * wrapped in a reactive `ref` and exposed as a read-only object.
 *
 * @return {Readonly<Ref<null|string>>} A read-only reactive reference containing the default current selected tenant owner ID, or `null` if no IDs are available.
 */
export const useDefaultCurrentSelectedTenantOwnerId = () => {
  const result = ref(null);

  const entities = store.getters.getEntityIds;
  if (Array.isArray(entities) && entities.length > 0) {
    result.value = entities[0];
  }

  return readonly(result);
};

/**
 * A Vue composable to retrieve and manage tenant owner IDs associated with the current user.
 *
 * This composable utilizes state management to fetch the entity IDs corresponding
 * to tenant ownership for the current user. It ensures reactivity by returning
 * a readonly reference containing the owner IDs. If no owner IDs are found,
 * the result remains an empty reactive array.
 *
 * @returns {Readonly<Ref<Array<unknown>>>} A readonly reactive reference containing tenant owner IDs.
 */
export const useCurrentUserTenantOwnerIds = () => {
  const result = ref([]);
  const ownerIds = toValue(store.getters.getEntityIds);
  if (Array.isArray(ownerIds) && ownerIds.length > 0) {
    result.value = ownerIds;
  }

  return readonly(result);
};

/**
 * Composable to check if the current route matches the "Program Manager View More" path.
 *
 * This function determines if the current route belongs to the "Program Manager" section
 * and optionally ends with "/view-more". The matching is performed using a regular
 * expression to validate the path.
 *
 * @returns {Readonly<Ref<boolean>>} A readonly ref containing `true` if the current route
 * matches the "Program Manager View More" path, otherwise `false`.
 */
const useIsProgramManagerViewMorePath = () => {
  const result = ref(false);

  const currentRoute = useCurrentRoute();
  result.value = !!toValue(currentRoute)?.match(PROGRAM_MANAGER_VIEW_MORE_REG_EXP);

  return readonly(result);
};

/**
 * A composable function that determines if the user has access to multi-tenancy features.
 * The function evaluates whether the user has tenant manager access or belongs to multiple tenants.
 *
 * @returns {Readonly<Ref<boolean>>} A readonly ref that indicates if the user has access to multi-tenancy features.
 */
export const useHasAccessToMultiTenancy = () => {
  const result = ref(false);

  const hasTenantManagerAccess = toValue(isTenantManager());
  const currentUserTenantOwnerIds = toValue(useCurrentUserTenantOwnerIds());
  if (hasTenantManagerAccess || (Array.isArray(currentUserTenantOwnerIds) && currentUserTenantOwnerIds.length > 0)) {
    result.value = true;
  }

  return readonly(result);
};

/**
 * Determines if the tenant selector feature is enabled based on specific conditions.
 *
 * The function evaluates several conditions such as current path, feature flags,
 * and tenant information to determine whether the tenant selector should be globally enabled.
 *
 * @return {Readonly<Ref<boolean>>} A readonly ref containing `true` if the tenant selector is enabled.
 */
export const useTenantSelectorEnabled = (showOnRoute = false) => {
  const tenantSelectorEnabled = ref(false);

  // TODO: Future work to enable the multiTenantSelector globally.
  const isProgramManagerPath = useIsProgramManagerViewMorePath();
  const tenantLibrariesEnabled = toValue(isTenantLibrariesFlagEnabled);

  tenantSelectorEnabled.value =
    (showOnRoute || toValue(isProgramManagerPath)) &&
    tenantLibrariesEnabled &&
    (isTenantManager() || hasMultipleEntities.value);

  return readonly(tenantSelectorEnabled);
};

/**
 * Provides parameters for multi-tenant entities based on the user's roles and permissions.
 *
 * The `useMultiTenantEntityParams` composable determines the appropriate tenant entity IDs to apply
 * for a user in a multi-tenant context. It takes into account whether the user has tenant management
 * permissions.
 *
 * - If the user has tenant management permissions, `result` will be an empty list,
 *   allowing access to all entities without filter.
 * - If the user does not have tenant management permissions, `result` will contain the
 *   entity IDs associated with the current user's tenant ownership.
 *
 * @returns {Readonly<Ref<Array>>} A readonly reference to the array of tenant entity IDs
 * either filtered by the current user's permissions or empty if the user is a tenant manager.
 */
export const useMultiTenantEntityParams = () => {
  const result = ref([]);

  const entityIds = toValue(useCurrentUserTenantOwnerIds());
  const tenantManagerPermissions = toValue(isTenantManager());

  // If user has TenantManager permissions don't filter on any ids.
  result.value = tenantManagerPermissions ? [] : entityIds;
  return readonly(result);
};

/**
 * A Vue composable that determines whether the Library Item Owner Selector feature is enabled.
 *
 * The function evaluates multiple conditions to set the `enabled` status for the selector.
 * It checks whether the multi-tenant feature flag is activated and if the current user's
 * tenant owner IDs exceed one or the user is recognized as a tenant manager. The final result
 * is returned as a readonly reference.
 *
 * @returns {Readonly<Ref<boolean>>} A readonly ref indicating whether the Library Item Owner Selector is enabled.
 */
export const useLibraryItemOwnerSelectorEnabled = () => {
  const result = ref(false);

  const multiTenantFeatureFlagEnabled = toValue(isTenantLibrariesFlagEnabled);
  const tenantManagerPermissions = toValue(isTenantManager());
  const tenantOwnerIds = toValue(useCurrentUserTenantOwnerIds());
  const hasMultiplenTenantOwners = Array.isArray(tenantOwnerIds) && tenantOwnerIds.length > 1;

  result.value = multiTenantFeatureFlagEnabled && (tenantManagerPermissions || hasMultiplenTenantOwners);
  return readonly(result);
};
